import type { ParamSpec } from "@cfg/design-sdk";
import type { ParamWidget, ParamWidgetProps } from "@cfg/brand-sdk";
import { createContext, useContext, useEffect, useId, useState, type ReactNode } from "react";

/* ----------------------------- widget registry ----------------------------- */

const RegistryCtx = createContext<Record<string, ParamWidget>>({});

export function WidgetRegistryProvider({ widgets, children }: { widgets?: Record<string, ParamWidget>; children: ReactNode }) {
  return <RegistryCtx.Provider value={widgets ?? {}}>{children}</RegistryCtx.Provider>;
}

/* ------------------------------ default widgets ---------------------------- */

function fmt(v: number, step?: number): string {
  if (!step || step >= 1) return Number.isInteger(v) ? String(v) : v.toFixed(2);
  const decimals = Math.min(4, Math.max(0, Math.ceil(-Math.log10(step))));
  return v.toFixed(decimals);
}

export function NumberWidget({ spec, value, onChange, disabled, invalid }: ParamWidgetProps<number>) {
  const id = useId();
  const [text, setText] = useState(fmt(value, spec.step));
  useEffect(() => setText(fmt(value, spec.step)), [value, spec.step]);
  const hasRange = spec.min !== undefined && spec.max !== undefined;
  const step = spec.step ?? (spec.type === "int" ? 1 : 0.1);
  const commit = (raw: string) => {
    const n = spec.type === "int" ? parseInt(raw, 10) : parseFloat(raw);
    if (Number.isNaN(n)) return setText(fmt(value, spec.step));
    let v = n;
    if (spec.min !== undefined) v = Math.max(spec.min, v);
    if (spec.max !== undefined) v = Math.min(spec.max, v);
    onChange(v);
  };
  return (
    <div className={`cfg-field cfg-field-number${invalid ? " is-invalid" : ""}`} data-param={spec.name}>
      <label className="cfg-label" htmlFor={id}>
        <span>{spec.label}</span>
        {spec.unit && <span className="cfg-unit">{spec.unit}</span>}
      </label>
      <div className="cfg-number-row">
        {hasRange && (
          <input
            className="cfg-slider"
            type="range"
            min={spec.min}
            max={spec.max}
            step={step}
            value={value}
            disabled={disabled}
            aria-label={spec.label}
            onChange={(e) => onChange(spec.type === "int" ? parseInt(e.target.value, 10) : parseFloat(e.target.value))}
          />
        )}
        <input
          id={id}
          className="cfg-input cfg-input-number"
          type="number"
          inputMode="decimal"
          min={spec.min}
          max={spec.max}
          step={step}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit((e.target as HTMLInputElement).value);
          }}
        />
      </div>
      {spec.description && <p className="cfg-help">{spec.description}</p>}
      {invalid && <p className="cfg-error">{invalid}</p>}
    </div>
  );
}

export function BoolWidget({ spec, value, onChange, disabled }: ParamWidgetProps<boolean>) {
  const id = useId();
  return (
    <div className="cfg-field cfg-field-bool" data-param={spec.name}>
      <label className="cfg-switch" htmlFor={id}>
        <input id={id} type="checkbox" role="switch" checked={value} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="cfg-switch-track" aria-hidden />
        <span className="cfg-label">{spec.label}</span>
      </label>
      {spec.description && <p className="cfg-help">{spec.description}</p>}
    </div>
  );
}

export function SelectWidget({ spec, value, onChange, disabled }: ParamWidgetProps<string>) {
  const id = useId();
  const options = spec.options ?? [];
  const segmented = options.length <= 4;
  return (
    <div className="cfg-field cfg-field-select" data-param={spec.name}>
      <label className="cfg-label" htmlFor={id}>
        {spec.label}
      </label>
      {segmented ? (
        <div className="cfg-segmented" role="radiogroup" aria-labelledby={id}>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={o.value === value}
              className={`cfg-segment${o.value === value ? " is-active" : ""}`}
              disabled={disabled}
              onClick={() => onChange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : (
        <select id={id} className="cfg-input" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
      {spec.description && <p className="cfg-help">{spec.description}</p>}
    </div>
  );
}

export function StringWidget({ spec, value, onChange, disabled }: ParamWidgetProps<string>) {
  const id = useId();
  return (
    <div className="cfg-field" data-param={spec.name}>
      <label className="cfg-label" htmlFor={id}>
        {spec.label}
      </label>
      <input id={id} className="cfg-input" type="text" value={value ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/* ----------------------------- field dispatcher ---------------------------- */

export interface ParamFieldProps {
  spec: ParamSpec;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
  invalid?: string;
  onFocus?: () => void;
}

/** Picks the widget: brand override by `spec.widget` or type, else the default for the type. */
export function ParamField({ spec, value, onChange, disabled, invalid, onFocus }: ParamFieldProps) {
  const registry = useContext(RegistryCtx);
  const Override = (spec.widget && registry[spec.widget]) || registry[`type:${spec.type}`];
  const props = { spec, value, onChange, disabled, invalid } as ParamWidgetProps;
  const body = Override ? (
    <Override {...props} />
  ) : spec.type === "number" || spec.type === "int" ? (
    <NumberWidget {...(props as ParamWidgetProps<number>)} />
  ) : spec.type === "bool" ? (
    <BoolWidget {...(props as ParamWidgetProps<boolean>)} />
  ) : spec.type === "select" ? (
    <SelectWidget {...(props as ParamWidgetProps<string>)} />
  ) : (
    <StringWidget {...(props as ParamWidgetProps<string>)} />
  );
  return (
    <div className="cfg-field-wrap" id={`cfg-param-${spec.name}`} onFocusCapture={onFocus} onPointerDownCapture={onFocus}>
      {body}
    </div>
  );
}
